import Text from '@/components/common/Text';
import { Theme } from '@/styles/Theme';
import type { MonthlyBillDetail } from '@/types/admin.types';
import styled from '@emotion/styled';

interface BillInfoCardProps {
  bill: MonthlyBillDetail;
}

const getStatusBadge = (status: MonthlyBillDetail['status']) => {
  const statusMap = {
    pending: { text: '대기', color: '#666' },
    sent: { text: '발송', color: '#2196F3' },
    pending_confirmation: { text: '확인 대기', color: '#FF9800' },
    paid: { text: '결제 완료', color: '#4CAF50' },
    overdue: { text: '연체', color: '#F44336' },
    failed: { text: '실패', color: '#F44336' },
    rejected: { text: '거부', color: '#666' },
    retry_pending: { text: '재시도 대기', color: '#FF5722' },
  };
  return statusMap[status] || statusMap.pending;
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

const formatDateTime = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatAmount = (amount: number) => {
  return `${amount.toLocaleString('ko-KR')}원`;
};

const BillInfoCard = ({ bill }: BillInfoCardProps) => {
  const statusBadge = getStatusBadge(bill.status);

  return (
    <Container>
      {/* Company Info Section */}
      {bill.user && (
        <Section>
          <SectionHeader>
            <Text fontSize='1.125rem' fontWeight={700}>
              회사 정보
            </Text>
          </SectionHeader>
          <InfoGrid>
            <InfoRow>
              <Label>회사명:</Label>
              <Value>{bill.user.company}</Value>
            </InfoRow>
            <InfoRow>
              <Label>이메일:</Label>
              <Value>{bill.user.email}</Value>
            </InfoRow>
            <InfoRow>
              <Label>전화번호:</Label>
              <Value>{bill.user.phone || '-'}</Value>
            </InfoRow>
            <InfoRow>
              <Label>구독 상태:</Label>
              <Value>{bill.user.subscription_status || '-'}</Value>
            </InfoRow>
          </InfoGrid>
        </Section>
      )}

      {/* Bill Info Section */}
      <Section>
        <SectionHeader>
          <Text fontSize='1.125rem' fontWeight={700}>
            청구서 정보
          </Text>
          <StatusBadge color={statusBadge.color}>
            {statusBadge.text}
          </StatusBadge>
        </SectionHeader>
        <InfoGrid>
          <InfoRow>
            <Label>청구서 ID:</Label>
            <Value>#{bill.id}</Value>
          </InfoRow>

          <InfoRow>
            <Label>결제 방식:</Label>
            <Value>{bill.payment_method === 'prepaid' ? '선불' : '후불'}</Value>
          </InfoRow>

          <InfoRow>
            <Label>청구 기간:</Label>
            <Value>
              {formatDate(bill.billing_period_start)} ~{' '}
              {formatDate(bill.billing_period_end)}
            </Value>
          </InfoRow>

          <InfoRow>
            <Label>총 금액:</Label>
            <Value fontWeight={700}>{formatAmount(bill.total_amount)}</Value>
          </InfoRow>

          <InfoRow>
            <Label>작업자 수:</Label>
            <Value>{bill.worker_count}명</Value>
          </InfoRow>

          <InfoRow>
            <Label>Prorated 비율:</Label>
            <Value>{(bill.prorated_ratio * 100).toFixed(2)}%</Value>
          </InfoRow>

          <InfoRow>
            <Label>생성일:</Label>
            <Value>{formatDateTime(bill.created_at)}</Value>
          </InfoRow>

          {bill.sent_at && (
            <InfoRow>
              <Label>발송일:</Label>
              <Value>{formatDateTime(bill.sent_at)}</Value>
            </InfoRow>
          )}

          {bill.confirmed_by_user_at && (
            <InfoRow>
              <Label>사용자 확인일:</Label>
              <Value>{formatDateTime(bill.confirmed_by_user_at)}</Value>
            </InfoRow>
          )}

          {bill.confirmed_by_admin_at && (
            <InfoRow>
              <Label>관리자 확인일:</Label>
              <Value>{formatDateTime(bill.confirmed_by_admin_at)}</Value>
            </InfoRow>
          )}
        </InfoGrid>

        {/* Notes and Additional Info */}
        {bill.user_confirmation_note && (
          <NoteSection>
            <Label>사용자 확인 메모:</Label>
            <NoteText>{bill.user_confirmation_note}</NoteText>
          </NoteSection>
        )}

        {bill.admin_confirmation_note && (
          <NoteSection>
            <Label>관리자 확인 메모:</Label>
            <NoteText>{bill.admin_confirmation_note}</NoteText>
          </NoteSection>
        )}

        {bill.rejection_reason && (
          <NoteSection>
            <Label>거부 사유:</Label>
            <NoteText color={Theme.colors.WARNING_RED}>{bill.rejection_reason}</NoteText>
          </NoteSection>
        )}

        {/* 결제 오류 정보 (PG Error Info) */}
        {(bill.status === 'failed' || bill.status === 'retry_pending') && (bill.pg_error_msg || bill.pg_error_code) && (
          <ErrorSection>
            <ErrorHeader>
              <ErrorIcon>⚠️</ErrorIcon>
              <Label>결제 오류 정보</Label>
            </ErrorHeader>
            {bill.pg_error_code && (
              <ErrorRow>
                <ErrorLabel>오류 코드:</ErrorLabel>
                <ErrorValue>{bill.pg_error_code}</ErrorValue>
              </ErrorRow>
            )}
            {bill.pg_error_msg && (
              <ErrorRow>
                <ErrorLabel>오류 메시지:</ErrorLabel>
                <ErrorValue>{bill.pg_error_msg}</ErrorValue>
              </ErrorRow>
            )}
            {bill.retry_count !== undefined && bill.retry_count > 0 && (
              <ErrorRow>
                <ErrorLabel>재시도 횟수:</ErrorLabel>
                <ErrorValue>{bill.retry_count}/3</ErrorValue>
              </ErrorRow>
            )}
            {bill.last_retry_at && (
              <ErrorRow>
                <ErrorLabel>마지막 재시도:</ErrorLabel>
                <ErrorValue>{formatDateTime(bill.last_retry_at)}</ErrorValue>
              </ErrorRow>
            )}
          </ErrorSection>
        )}

        {bill.user_confirmation_image_url && (
          <ImageSection>
            <Label>영수증 이미지:</Label>
            <ImageLink
              href={bill.user_confirmation_image_url}
              target='_blank'
              rel='noopener noreferrer'
            >
              이미지 보기
            </ImageLink>
          </ImageSection>
        )}
      </Section>
    </Container>
  );
};

const Container = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 0.75rem;
  border-bottom: 0.0625rem solid ${Theme.colors.G_4};
`;

const StatusBadge = styled.span<{ color: string }>`
  padding: 0.375rem 0.75rem;
  border-radius: 0.25rem;
  background-color: ${(props) => props.color};
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Label = styled(Text)`
  font-size: 0.875rem;
  color: ${Theme.colors.G_2};
  font-weight: 600;
`;

const Value = styled(Text)<{ fontWeight?: number }>`
  font-size: 0.875rem;
  color: ${Theme.colors.G_1};
  font-weight: ${(props) => props.fontWeight || 400};
`;

const NoteSection = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 0.0625rem solid ${Theme.colors.G_4};
`;

const NoteText = styled(Text)<{ color?: string }>`
  font-size: 0.875rem;
  color: ${(props) => props.color || Theme.colors.G_1};
  margin-top: 0.5rem;
  white-space: pre-wrap;
`;

const ImageSection = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 0.0625rem solid ${Theme.colors.G_4};
`;

const ImageLink = styled.a`
  color: ${Theme.colors.B_1};
  text-decoration: none;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  display: inline-block;

  &:hover {
    text-decoration: underline;
  }
`;

const ErrorSection = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: #FFF3E0;
  border-radius: 0.5rem;
  border-left: 0.25rem solid #FF5722;
`;

const ErrorHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`;

const ErrorIcon = styled.span`
  font-size: 1.25rem;
`;

const ErrorRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.375rem;
  align-items: baseline;
`;

const ErrorLabel = styled.span`
  font-size: 0.8125rem;
  color: ${Theme.colors.G_2};
  font-weight: 600;
  min-width: 6rem;
`;

const ErrorValue = styled.span`
  font-size: 0.8125rem;
  color: #D84315;
  font-weight: 500;
`;

export default BillInfoCard;

